import { useCallback, useContext } from 'react';
import { EventsContext } from '../../Components/Events/EventsProvider';
import type { SortDirection } from './types';

const isOption = (value: unknown): value is { id?: unknown; label?: unknown } =>
  typeof value === 'object' && value !== null;

/**
 * Isolates list analytics: `list.<name>.sort` with
 * `{ key, direction }`, and `list.<name>.filter.<added|removed>` with `{ key }`, plus the selection
 * (`selectionId`, and `selectionLabel` for a `{ id, label }` option) when a filter is set. Reads the
 * context directly (not `useEvents`) so it no-ops when no provider is mounted. No-ops entirely
 * without a `name`.
 */
export const useListAnalytics = (
  name: string | undefined
): {
  trackSort: (field: string, direction: SortDirection) => void;
  trackFilter: (key: PropertyKey, value: unknown) => void;
} => {
  const events = useContext(EventsContext);

  const track = useCallback(
    (event: string, payload: Record<string, unknown>) => {
      if (name && events) events.track(`list.${name}.${event}`, payload);
    },
    [name, events]
  );

  const trackSort = useCallback(
    (field: string, direction: SortDirection) => track('sort', { key: field, direction }),
    [track]
  );

  const trackFilter = useCallback(
    (key: PropertyKey, value: unknown) => {
      if (!value) {
        track('filter.removed', { key: String(key) });
        return;
      }
      track('filter.added', {
        key: String(key),
        selectionId: isOption(value) && value.id != null ? value.id : value,
        selectionLabel: isOption(value) ? value.label : undefined,
      });
    },
    [track]
  );

  return { trackSort, trackFilter };
};
